/**
 * Update Password Page Component
 *
 * Shown when user clicks password reset link from email.
 * Uses the platform-appropriate shared authentication layout.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Loading01 } from '@untitled-ui/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthRouteLayout } from '@/components/auth/auth-route-layout';
import { BrandLoader } from '@/components/shared/brand-loader';
import { getAuthPageChrome } from '@/components/auth/auth-page-chrome';
import {
  RecoveryCodeField,
  RevealablePasswordField,
} from '@/components/auth/update-password-fields';
import { createFormSchema } from '@/components/auth/update-password-schema';
import { HookForm } from '@/components/base/form/hook-form';
import { Button } from '@/components/base/buttons/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useRecoveryMfaStepUp } from '@/hooks/use-recovery-mfa-step-up';
import { formatWeakPasswordError } from '@/lib/password-utils';

import type { UpdatePasswordFormData as FormData } from '@/components/auth/update-password-schema';
import type { RecoveryStepUpOutcome } from '@/hooks/use-recovery-mfa-step-up';

const ERROR_TITLE_KEY = 'updatePassword.errorTitle';

const CODE_ERROR_KEYS: Partial<Record<RecoveryStepUpOutcome, string>> = {
  code_required: 'updatePassword.codeRequired',
  invalid_code: 'updatePassword.invalidCode',
};

const UpdatePassword = () => {
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const { check: checkMfa, stepUp, isCodeRequired } = useRecoveryMfaStepUp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const chrome = getAuthPageChrome();

  const formSchema = useMemo(
    () =>
      createFormSchema({
        minLength: t('updatePassword.minLength'),
        uppercase: t('updatePassword.uppercase'),
        lowercase: t('updatePassword.lowercase'),
        number: t('updatePassword.number'),
        mismatch: t('updatePassword.mismatch'),
      }),
    [t],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
      totpCode: '',
    },
  });

  useEffect(() => {
    // Check if we have a valid recovery session
    const checkSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error || !session) {
        setIsValidToken(false);
        toast({
          title: t('updatePassword.invalidLinkTitle'),
          description: t('updatePassword.invalidLinkDescription'),
          variant: 'destructive',
        });
      } else {
        await checkMfa();
        setIsValidToken(true);
      }
    };

    void checkSession();
  }, [toast, t, checkMfa]);

  /** False stops the submit; the reason is already on screen. */
  const satisfyMfa = async (totpCode: string): Promise<boolean> => {
    const outcome = await stepUp(totpCode);
    const codeErrorKey = CODE_ERROR_KEYS[outcome];

    if (outcome === 'check_failed') {
      toast({
        title: t(ERROR_TITLE_KEY),
        description: t('updatePassword.mfaCheckFailed'),
        variant: 'destructive',
      });
    } else if (codeErrorKey) {
      form.setError('totpCode', { type: 'manual', message: t(codeErrorKey) });
    }

    return outcome === 'ready';
  };

  const handleUpdatePassword = async (values: FormData) => {
    setIsLoading(true);

    try {
      if (!(await satisfyMfa(values.totpCode))) {
        return;
      }

      // password_set marks the account as holding its own credential, so an
      // invitation later offers Accept instead of the first-time setup form.
      const { error } = await supabase.auth.updateUser({
        password: values.password,
        data: { password_set: true },
      });

      if (error) {
        // Format the error message, especially for weak password errors
        const errorMessage = formatWeakPasswordError(error);

        toast({
          title: t(ERROR_TITLE_KEY),
          description: errorMessage,
          variant: 'destructive',
        });

        // If it's a weak password error, also set it as a form field error
        if (error?.name === 'AuthWeakPasswordError' || error?.message?.includes('weak')) {
          form.setError('password', {
            type: 'manual',
            message: errorMessage,
          });
        }
      } else {
        toast({
          title: t('updatePassword.updatedTitle'),
          description: t('updatePassword.updatedDescription'),
        });

        // Redirect to login
        setTimeout(() => {
          Promise.resolve(navigate('/auth/login', { replace: true })).catch(() => null);
        }, 1500);
      }
    } catch (error: unknown) {
      console.error('Password update error:', error);

      // Format the error message
      const authError = error as { name?: string; message?: string };
      const errorMessage = formatWeakPasswordError(authError);

      toast({
        title: t(ERROR_TITLE_KEY),
        description: errorMessage,
        variant: 'destructive',
      });

      // If it's a weak password error, set the form field error
      if (authError.name === 'AuthWeakPasswordError' || authError.message?.includes('weak')) {
        form.setError('password', {
          type: 'manual',
          message: errorMessage,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidToken === null) {
    return (
      <AuthRouteLayout>
        <div className="flex items-center justify-center py-8">
          <BrandLoader size="lg" />
        </div>
      </AuthRouteLayout>
    );
  }

  if (isValidToken === false) {
    return (
      <AuthRouteLayout>
        <div className="flex flex-col gap-4">
          <h1 className={chrome.heading}>{t('updatePassword.linkExpiredHeading')}</h1>
          <p className={chrome.description}>{t('updatePassword.linkExpiredDescription')}</p>
          <Button
            className={chrome.primaryButton}
            size="md"
            onClick={() => {
              Promise.resolve(navigate('/auth/reset-password')).catch(() => null);
            }}
          >
            {t('updatePassword.requestNewLink')}
          </Button>
        </div>
      </AuthRouteLayout>
    );
  }

  return (
    <AuthRouteLayout>
      <HookForm
        className="flex flex-col gap-4"
        form={form}
        onSubmit={(e) => {
          form
            .handleSubmit(handleUpdatePassword)(e)
            .catch(() => {
              /* handled by react-hook-form */
            });
        }}
      >
        <div className="mb-2">
          <h1 className={chrome.heading}>{t('updatePassword.heading')}</h1>
          <p className={chrome.description}>{t('updatePassword.description')}</p>
        </div>

        <RevealablePasswordField
          chrome={chrome}
          control={form.control}
          label={t('updatePassword.newPasswordLabel')}
          name="password"
          placeholder={t('updatePassword.passwordPlaceholder')}
        />

        <RevealablePasswordField
          chrome={chrome}
          control={form.control}
          label={t('updatePassword.confirmLabel')}
          name="confirmPassword"
          placeholder={t('updatePassword.confirmPlaceholder')}
        />

        {isCodeRequired ? <RecoveryCodeField chrome={chrome} control={form.control} /> : null}

        <Button className={chrome.primaryButton} isDisabled={isLoading} size="md" type="submit">
          {isLoading ? (
            <>
              <Loading01 aria-hidden="true" className="mr-2 size-4 animate-spin" />
              {t('updatePassword.updating')}
            </>
          ) : (
            t('updatePassword.submit')
          )}
        </Button>
      </HookForm>

      <div className="mt-6 text-center">
        <span className={chrome.mutedText}>{t('signInPrompt.remember')} </span>
        <Button
          className={chrome.link}
          color="link-color"
          size="md"
          onClick={() => {
            Promise.resolve(navigate('/auth/login')).catch(() => null);
          }}
        >
          {t('signInPrompt.signIn')}
        </Button>
      </div>
    </AuthRouteLayout>
  );
};

export default UpdatePassword;
